/**
 * This code was AUTOGENERATED using the Codama library.
 * Please DO NOT EDIT THIS FILE, instead use visitors
 * to add features, then rerun Codama to update it.
 *
 * @see https://github.com/codama-idl/codama
 */

import {
  combineCodec,
  fixDecoderSize,
  fixEncoderSize,
  getAddressEncoder,
  getBooleanDecoder,
  getBooleanEncoder,
  getBytesDecoder,
  getBytesEncoder,
  getProgramDerivedAddress,
  getStructDecoder,
  getStructEncoder,
  getU128Decoder,
  getU128Encoder,
  getU64Decoder,
  getU64Encoder,
  SOLANA_ERROR__PROGRAM_CLIENTS__INSUFFICIENT_ACCOUNT_METAS,
  SolanaError,
  transformEncoder,
  type AccountMeta,
  type AccountSignerMeta,
  type Address,
  type FixedSizeCodec,
  type FixedSizeDecoder,
  type FixedSizeEncoder,
  type Instruction,
  type InstructionWithAccounts,
  type InstructionWithData,
  type ReadonlyAccount,
  type ReadonlySignerAccount,
  type ReadonlyUint8Array,
  type TransactionSigner,
  type WritableAccount,
} from '@solana/kit';
import {
  getAccountMetaFactory,
  getAddressFromResolvedInstructionAccount,
  type ResolvedInstructionAccount,
} from '@solana/program-client-core';
import { CPMM_PROGRAM_ADDRESS } from '../programs';

export const REMOVE_LIQUIDITY_DISCRIMINATOR = new Uint8Array([
  80, 85, 209, 72, 24, 206, 177, 108,
]);

export function getRemoveLiquidityDiscriminatorBytes() {
  return fixEncoderSize(getBytesEncoder(), 8).encode(
    REMOVE_LIQUIDITY_DISCRIMINATOR,
  );
}

export type RemoveLiquidityInstruction<
  TProgram extends string = typeof CPMM_PROGRAM_ADDRESS,
  TAccountConfig extends string | AccountMeta<string> = string,
  TAccountPool extends string | AccountMeta<string> = string,
  TAccountPosition extends string | AccountMeta<string> = string,
  TAccountProtocolFeePosition extends string | AccountMeta<string> = string,
  TAccountOwner extends string | AccountMeta<string> = string,
  TAccountAuthority extends string | AccountMeta<string> = string,
  TAccountVault0 extends string | AccountMeta<string> = string,
  TAccountVault1 extends string | AccountMeta<string> = string,
  TAccountToken0Mint extends string | AccountMeta<string> = string,
  TAccountToken1Mint extends string | AccountMeta<string> = string,
  TAccountUser0 extends string | AccountMeta<string> = string,
  TAccountUser1 extends string | AccountMeta<string> = string,
  TAccountToken0Program extends string | AccountMeta<string> = string,
  TAccountToken1Program extends string | AccountMeta<string> = string,
  TAccountOracle extends string | AccountMeta<string> = string,
  TRemainingAccounts extends readonly AccountMeta<string>[] = [],
> = Instruction<TProgram> &
  InstructionWithData<ReadonlyUint8Array> &
  InstructionWithAccounts<
    [
      TAccountConfig extends string
        ? ReadonlyAccount<TAccountConfig>
        : TAccountConfig,
      TAccountPool extends string
        ? WritableAccount<TAccountPool>
        : TAccountPool,
      TAccountPosition extends string
        ? WritableAccount<TAccountPosition>
        : TAccountPosition,
      TAccountProtocolFeePosition extends string
        ? WritableAccount<TAccountProtocolFeePosition>
        : TAccountProtocolFeePosition,
      TAccountOwner extends string
        ? ReadonlySignerAccount<TAccountOwner> &
            AccountSignerMeta<TAccountOwner>
        : TAccountOwner,
      TAccountAuthority extends string
        ? ReadonlyAccount<TAccountAuthority>
        : TAccountAuthority,
      TAccountVault0 extends string
        ? WritableAccount<TAccountVault0>
        : TAccountVault0,
      TAccountVault1 extends string
        ? WritableAccount<TAccountVault1>
        : TAccountVault1,
      TAccountToken0Mint extends string
        ? ReadonlyAccount<TAccountToken0Mint>
        : TAccountToken0Mint,
      TAccountToken1Mint extends string
        ? ReadonlyAccount<TAccountToken1Mint>
        : TAccountToken1Mint,
      TAccountUser0 extends string
        ? WritableAccount<TAccountUser0>
        : TAccountUser0,
      TAccountUser1 extends string
        ? WritableAccount<TAccountUser1>
        : TAccountUser1,
      TAccountToken0Program extends string
        ? ReadonlyAccount<TAccountToken0Program>
        : TAccountToken0Program,
      TAccountToken1Program extends string
        ? ReadonlyAccount<TAccountToken1Program>
        : TAccountToken1Program,
      TAccountOracle extends string
        ? WritableAccount<TAccountOracle>
        : TAccountOracle,
      ...TRemainingAccounts,
    ]
  >;

export type RemoveLiquidityInstructionData = {
  discriminator: ReadonlyUint8Array;
  sharesIn: bigint;
  minAmount0Out: bigint;
  minAmount1Out: bigint;
  updateOracle: boolean;
};

export type RemoveLiquidityInstructionDataArgs = {
  sharesIn: number | bigint;
  minAmount0Out: number | bigint;
  minAmount1Out: number | bigint;
  updateOracle: boolean;
};

export function getRemoveLiquidityInstructionDataEncoder(): FixedSizeEncoder<RemoveLiquidityInstructionDataArgs> {
  return transformEncoder(
    getStructEncoder([
      ['discriminator', fixEncoderSize(getBytesEncoder(), 8)],
      ['sharesIn', getU128Encoder()],
      ['minAmount0Out', getU64Encoder()],
      ['minAmount1Out', getU64Encoder()],
      ['updateOracle', getBooleanEncoder()],
    ]),
    (value) => ({ ...value, discriminator: REMOVE_LIQUIDITY_DISCRIMINATOR }),
  );
}

export function getRemoveLiquidityInstructionDataDecoder(): FixedSizeDecoder<RemoveLiquidityInstructionData> {
  return getStructDecoder([
    ['discriminator', fixDecoderSize(getBytesDecoder(), 8)],
    ['sharesIn', getU128Decoder()],
    ['minAmount0Out', getU64Decoder()],
    ['minAmount1Out', getU64Decoder()],
    ['updateOracle', getBooleanDecoder()],
  ]);
}

export function getRemoveLiquidityInstructionDataCodec(): FixedSizeCodec<
  RemoveLiquidityInstructionDataArgs,
  RemoveLiquidityInstructionData
> {
  return combineCodec(
    getRemoveLiquidityInstructionDataEncoder(),
    getRemoveLiquidityInstructionDataDecoder(),
  );
}

export type RemoveLiquidityAsyncInput<
  TAccountConfig extends string = string,
  TAccountPool extends string = string,
  TAccountPosition extends string = string,
  TAccountProtocolFeePosition extends string = string,
  TAccountOwner extends string = string,
  TAccountAuthority extends string = string,
  TAccountVault0 extends string = string,
  TAccountVault1 extends string = string,
  TAccountToken0Mint extends string = string,
  TAccountToken1Mint extends string = string,
  TAccountUser0 extends string = string,
  TAccountUser1 extends string = string,
  TAccountToken0Program extends string = string,
  TAccountToken1Program extends string = string,
  TAccountOracle extends string = string,
> = {
  config: Address<TAccountConfig>;
  pool: Address<TAccountPool>;
  position: Address<TAccountPosition>;
  protocolFeePosition: Address<TAccountProtocolFeePosition>;
  owner: TransactionSigner<TAccountOwner>;
  authority?: Address<TAccountAuthority>;
  vault0: Address<TAccountVault0>;
  vault1: Address<TAccountVault1>;
  token0Mint: Address<TAccountToken0Mint>;
  token1Mint: Address<TAccountToken1Mint>;
  user0: Address<TAccountUser0>;
  user1: Address<TAccountUser1>;
  token0Program: Address<TAccountToken0Program>;
  token1Program: Address<TAccountToken1Program>;
  oracle?: Address<TAccountOracle>;
  sharesIn: RemoveLiquidityInstructionDataArgs['sharesIn'];
  minAmount0Out: RemoveLiquidityInstructionDataArgs['minAmount0Out'];
  minAmount1Out: RemoveLiquidityInstructionDataArgs['minAmount1Out'];
  updateOracle: RemoveLiquidityInstructionDataArgs['updateOracle'];
};

export async function getRemoveLiquidityInstructionAsync<
  TAccountConfig extends string,
  TAccountPool extends string,
  TAccountPosition extends string,
  TAccountProtocolFeePosition extends string,
  TAccountOwner extends string,
  TAccountAuthority extends string,
  TAccountVault0 extends string,
  TAccountVault1 extends string,
  TAccountToken0Mint extends string,
  TAccountToken1Mint extends string,
  TAccountUser0 extends string,
  TAccountUser1 extends string,
  TAccountToken0Program extends string,
  TAccountToken1Program extends string,
  TAccountOracle extends string,
  TProgramAddress extends Address = typeof CPMM_PROGRAM_ADDRESS,
>(
  input: RemoveLiquidityAsyncInput<
    TAccountConfig,
    TAccountPool,
    TAccountPosition,
    TAccountProtocolFeePosition,
    TAccountOwner,
    TAccountAuthority,
    TAccountVault0,
    TAccountVault1,
    TAccountToken0Mint,
    TAccountToken1Mint,
    TAccountUser0,
    TAccountUser1,
    TAccountToken0Program,
    TAccountToken1Program,
    TAccountOracle
  >,
  config?: { programAddress?: TProgramAddress },
): Promise<
  RemoveLiquidityInstruction<
    TProgramAddress,
    TAccountConfig,
    TAccountPool,
    TAccountPosition,
    TAccountProtocolFeePosition,
    TAccountOwner,
    TAccountAuthority,
    TAccountVault0,
    TAccountVault1,
    TAccountToken0Mint,
    TAccountToken1Mint,
    TAccountUser0,
    TAccountUser1,
    TAccountToken0Program,
    TAccountToken1Program,
    TAccountOracle
  >
> {
  // Program address.
  const programAddress = config?.programAddress ?? CPMM_PROGRAM_ADDRESS;

  // Original accounts.
  const originalAccounts = {
    config: { value: input.config ?? null, isWritable: false },
    pool: { value: input.pool ?? null, isWritable: true },
    position: { value: input.position ?? null, isWritable: true },
    protocolFeePosition: {
      value: input.protocolFeePosition ?? null,
      isWritable: true,
    },
    owner: { value: input.owner ?? null, isWritable: false },
    authority: { value: input.authority ?? null, isWritable: false },
    vault0: { value: input.vault0 ?? null, isWritable: true },
    vault1: { value: input.vault1 ?? null, isWritable: true },
    token0Mint: { value: input.token0Mint ?? null, isWritable: false },
    token1Mint: { value: input.token1Mint ?? null, isWritable: false },
    user0: { value: input.user0 ?? null, isWritable: true },
    user1: { value: input.user1 ?? null, isWritable: true },
    token0Program: { value: input.token0Program ?? null, isWritable: false },
    token1Program: { value: input.token1Program ?? null, isWritable: false },
    oracle: { value: input.oracle ?? null, isWritable: true },
  };
  const accounts = originalAccounts as Record<
    keyof typeof originalAccounts,
    ResolvedInstructionAccount
  >;

  // Original args.
  const args = { ...input };

  // Resolve default values.
  if (!accounts.authority.value) {
    accounts.authority.value = await getProgramDerivedAddress({
      programAddress,
      seeds: [
        getBytesEncoder().encode(
          new Uint8Array([97, 117, 116, 104, 111, 114, 105, 116, 121]),
        ),
        getAddressEncoder().encode(
          getAddressFromResolvedInstructionAccount('pool', accounts.pool.value),
        ),
      ],
    });
  }

  const getAccountMeta = getAccountMetaFactory(programAddress, 'programId');
  return Object.freeze({
    accounts: [
      getAccountMeta('config', accounts.config),
      getAccountMeta('pool', accounts.pool),
      getAccountMeta('position', accounts.position),
      getAccountMeta('protocolFeePosition', accounts.protocolFeePosition),
      getAccountMeta('owner', accounts.owner),
      getAccountMeta('authority', accounts.authority),
      getAccountMeta('vault0', accounts.vault0),
      getAccountMeta('vault1', accounts.vault1),
      getAccountMeta('token0Mint', accounts.token0Mint),
      getAccountMeta('token1Mint', accounts.token1Mint),
      getAccountMeta('user0', accounts.user0),
      getAccountMeta('user1', accounts.user1),
      getAccountMeta('token0Program', accounts.token0Program),
      getAccountMeta('token1Program', accounts.token1Program),
      getAccountMeta('oracle', accounts.oracle),
    ],
    data: getRemoveLiquidityInstructionDataEncoder().encode(
      args as RemoveLiquidityInstructionDataArgs,
    ),
    programAddress,
  } as RemoveLiquidityInstruction<
    TProgramAddress,
    TAccountConfig,
    TAccountPool,
    TAccountPosition,
    TAccountProtocolFeePosition,
    TAccountOwner,
    TAccountAuthority,
    TAccountVault0,
    TAccountVault1,
    TAccountToken0Mint,
    TAccountToken1Mint,
    TAccountUser0,
    TAccountUser1,
    TAccountToken0Program,
    TAccountToken1Program,
    TAccountOracle
  >);
}

export type RemoveLiquidityInput<
  TAccountConfig extends string = string,
  TAccountPool extends string = string,
  TAccountPosition extends string = string,
  TAccountProtocolFeePosition extends string = string,
  TAccountOwner extends string = string,
  TAccountAuthority extends string = string,
  TAccountVault0 extends string = string,
  TAccountVault1 extends string = string,
  TAccountToken0Mint extends string = string,
  TAccountToken1Mint extends string = string,
  TAccountUser0 extends string = string,
  TAccountUser1 extends string = string,
  TAccountToken0Program extends string = string,
  TAccountToken1Program extends string = string,
  TAccountOracle extends string = string,
> = {
  config: Address<TAccountConfig>;
  pool: Address<TAccountPool>;
  position: Address<TAccountPosition>;
  protocolFeePosition: Address<TAccountProtocolFeePosition>;
  owner: TransactionSigner<TAccountOwner>;
  authority: Address<TAccountAuthority>;
  vault0: Address<TAccountVault0>;
  vault1: Address<TAccountVault1>;
  token0Mint: Address<TAccountToken0Mint>;
  token1Mint: Address<TAccountToken1Mint>;
  user0: Address<TAccountUser0>;
  user1: Address<TAccountUser1>;
  token0Program: Address<TAccountToken0Program>;
  token1Program: Address<TAccountToken1Program>;
  oracle?: Address<TAccountOracle>;
  sharesIn: RemoveLiquidityInstructionDataArgs['sharesIn'];
  minAmount0Out: RemoveLiquidityInstructionDataArgs['minAmount0Out'];
  minAmount1Out: RemoveLiquidityInstructionDataArgs['minAmount1Out'];
  updateOracle: RemoveLiquidityInstructionDataArgs['updateOracle'];
};

export function getRemoveLiquidityInstruction<
  TAccountConfig extends string,
  TAccountPool extends string,
  TAccountPosition extends string,
  TAccountProtocolFeePosition extends string,
  TAccountOwner extends string,
  TAccountAuthority extends string,
  TAccountVault0 extends string,
  TAccountVault1 extends string,
  TAccountToken0Mint extends string,
  TAccountToken1Mint extends string,
  TAccountUser0 extends string,
  TAccountUser1 extends string,
  TAccountToken0Program extends string,
  TAccountToken1Program extends string,
  TAccountOracle extends string,
  TProgramAddress extends Address = typeof CPMM_PROGRAM_ADDRESS,
>(
  input: RemoveLiquidityInput<
    TAccountConfig,
    TAccountPool,
    TAccountPosition,
    TAccountProtocolFeePosition,
    TAccountOwner,
    TAccountAuthority,
    TAccountVault0,
    TAccountVault1,
    TAccountToken0Mint,
    TAccountToken1Mint,
    TAccountUser0,
    TAccountUser1,
    TAccountToken0Program,
    TAccountToken1Program,
    TAccountOracle
  >,
  config?: { programAddress?: TProgramAddress },
): RemoveLiquidityInstruction<
  TProgramAddress,
  TAccountConfig,
  TAccountPool,
  TAccountPosition,
  TAccountProtocolFeePosition,
  TAccountOwner,
  TAccountAuthority,
  TAccountVault0,
  TAccountVault1,
  TAccountToken0Mint,
  TAccountToken1Mint,
  TAccountUser0,
  TAccountUser1,
  TAccountToken0Program,
  TAccountToken1Program,
  TAccountOracle
> {
  // Program address.
  const programAddress = config?.programAddress ?? CPMM_PROGRAM_ADDRESS;

  // Original accounts.
  const originalAccounts = {
    config: { value: input.config ?? null, isWritable: false },
    pool: { value: input.pool ?? null, isWritable: true },
    position: { value: input.position ?? null, isWritable: true },
    protocolFeePosition: {
      value: input.protocolFeePosition ?? null,
      isWritable: true,
    },
    owner: { value: input.owner ?? null, isWritable: false },
    authority: { value: input.authority ?? null, isWritable: false },
    vault0: { value: input.vault0 ?? null, isWritable: true },
    vault1: { value: input.vault1 ?? null, isWritable: true },
    token0Mint: { value: input.token0Mint ?? null, isWritable: false },
    token1Mint: { value: input.token1Mint ?? null, isWritable: false },
    user0: { value: input.user0 ?? null, isWritable: true },
    user1: { value: input.user1 ?? null, isWritable: true },
    token0Program: { value: input.token0Program ?? null, isWritable: false },
    token1Program: { value: input.token1Program ?? null, isWritable: false },
    oracle: { value: input.oracle ?? null, isWritable: true },
  };
  const accounts = originalAccounts as Record<
    keyof typeof originalAccounts,
    ResolvedInstructionAccount
  >;

  // Original args.
  const args = { ...input };

  const getAccountMeta = getAccountMetaFactory(programAddress, 'programId');
  return Object.freeze({
    accounts: [
      getAccountMeta('config', accounts.config),
      getAccountMeta('pool', accounts.pool),
      getAccountMeta('position', accounts.position),
      getAccountMeta('protocolFeePosition', accounts.protocolFeePosition),
      getAccountMeta('owner', accounts.owner),
      getAccountMeta('authority', accounts.authority),
      getAccountMeta('vault0', accounts.vault0),
      getAccountMeta('vault1', accounts.vault1),
      getAccountMeta('token0Mint', accounts.token0Mint),
      getAccountMeta('token1Mint', accounts.token1Mint),
      getAccountMeta('user0', accounts.user0),
      getAccountMeta('user1', accounts.user1),
      getAccountMeta('token0Program', accounts.token0Program),
      getAccountMeta('token1Program', accounts.token1Program),
      getAccountMeta('oracle', accounts.oracle),
    ],
    data: getRemoveLiquidityInstructionDataEncoder().encode(
      args as RemoveLiquidityInstructionDataArgs,
    ),
    programAddress,
  } as RemoveLiquidityInstruction<
    TProgramAddress,
    TAccountConfig,
    TAccountPool,
    TAccountPosition,
    TAccountProtocolFeePosition,
    TAccountOwner,
    TAccountAuthority,
    TAccountVault0,
    TAccountVault1,
    TAccountToken0Mint,
    TAccountToken1Mint,
    TAccountUser0,
    TAccountUser1,
    TAccountToken0Program,
    TAccountToken1Program,
    TAccountOracle
  >);
}

export type ParsedRemoveLiquidityInstruction<
  TProgram extends string = typeof CPMM_PROGRAM_ADDRESS,
  TAccountMetas extends readonly AccountMeta[] = readonly AccountMeta[],
> = {
  programAddress: Address<TProgram>;
  accounts: {
    config: TAccountMetas[0];
    pool: TAccountMetas[1];
    position: TAccountMetas[2];
    protocolFeePosition: TAccountMetas[3];
    owner: TAccountMetas[4];
    authority: TAccountMetas[5];
    vault0: TAccountMetas[6];
    vault1: TAccountMetas[7];
    token0Mint: TAccountMetas[8];
    token1Mint: TAccountMetas[9];
    user0: TAccountMetas[10];
    user1: TAccountMetas[11];
    token0Program: TAccountMetas[12];
    token1Program: TAccountMetas[13];
    oracle?: TAccountMetas[14] | undefined;
  };
  data: RemoveLiquidityInstructionData;
};

export function parseRemoveLiquidityInstruction<
  TProgram extends string,
  TAccountMetas extends readonly AccountMeta[],
>(
  instruction: Instruction<TProgram> &
    InstructionWithAccounts<TAccountMetas> &
    InstructionWithData<ReadonlyUint8Array>,
): ParsedRemoveLiquidityInstruction<TProgram, TAccountMetas> {
  if (instruction.accounts.length < 15) {
    throw new SolanaError(
      SOLANA_ERROR__PROGRAM_CLIENTS__INSUFFICIENT_ACCOUNT_METAS,
      {
        actualAccountMetas: instruction.accounts.length,
        expectedAccountMetas: 15,
      },
    );
  }
  let accountIndex = 0;
  const getNextAccount = () => {
    const accountMeta = (instruction.accounts as TAccountMetas)[accountIndex]!;
    accountIndex += 1;
    return accountMeta;
  };
  const getNextOptionalAccount = () => {
    const accountMeta = getNextAccount();
    return accountMeta.address === CPMM_PROGRAM_ADDRESS
      ? undefined
      : accountMeta;
  };
  return {
    programAddress: instruction.programAddress,
    accounts: {
      config: getNextAccount(),
      pool: getNextAccount(),
      position: getNextAccount(),
      protocolFeePosition: getNextAccount(),
      owner: getNextAccount(),
      authority: getNextAccount(),
      vault0: getNextAccount(),
      vault1: getNextAccount(),
      token0Mint: getNextAccount(),
      token1Mint: getNextAccount(),
      user0: getNextAccount(),
      user1: getNextAccount(),
      token0Program: getNextAccount(),
      token1Program: getNextAccount(),
      oracle: getNextOptionalAccount(),
    },
    data: getRemoveLiquidityInstructionDataDecoder().decode(instruction.data),
  };
}
